import { cn, controlClasses, Select } from '@fruuit/ui'
import { Search, X } from 'lucide-react'
import type { MouseEvent } from 'react'
import type { SectionMatch } from './search'

export function sectionNumber(index: number): string {
  return String(index + 1).padStart(2, '0')
}

interface FilterProps {
  query: string
  onQuery: (value: string) => void
  id: string
}

/** Filter box shared by the desktop table of contents and the mobile bar. */
export function DocsFilter({ query, onQuery, id }: FilterProps) {
  return (
    <div className="relative">
      <label htmlFor={id} className="sr-only">
        Filter sections
      </label>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-sage"
      />
      <input
        id={id}
        type="search"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        placeholder="Filter sections"
        autoComplete="off"
        className={cn(controlClasses, 'h-10 pr-9 pl-9 [&::-webkit-search-cancel-button]:hidden')}
      />
      {query ? (
        <button
          type="button"
          onClick={() => onQuery('')}
          aria-label="Clear filter"
          className="absolute top-1/2 right-2 grid size-6 -translate-y-1/2 place-items-center rounded-sm text-sage hover:bg-wash hover:text-ink focus-visible:outline-2 focus-visible:outline-teal"
        >
          <X aria-hidden="true" className="size-3.5" />
        </button>
      ) : null}
    </div>
  )
}

interface TocProps {
  matches: SectionMatch[]
  /** Position of each section in the full handbook (numbers stay stable while filtering). */
  numberOf: (id: string) => number
  activeId: string | null
  onJump: (id: string) => void
  query: string
  onQuery: (value: string) => void
  total: number
}

/** Desktop table of contents: sticky, with the filter on top. */
export function DocsToc({ matches, numberOf, activeId, onJump, query, onQuery, total }: TocProps) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>, id: string) {
    event.preventDefault()
    onJump(id)
  }
  return (
    <nav aria-label="Handbook contents" className="flex flex-col gap-4">
      <DocsFilter query={query} onQuery={onQuery} id="docs-filter" />
      <p className="px-1 text-xs text-sage" aria-live="polite">
        {query ? `${matches.length} of ${total} sections match` : `${total} sections`}
      </p>
      <ol className="flex flex-col gap-0.5">
        {matches.map(({ section, inTitle }) => {
          const Icon = section.icon
          const active = activeId === section.id
          return (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                onClick={(event) => handleClick(event, section.id)}
                aria-current={active ? 'location' : undefined}
                className={cn(
                  'group flex items-start gap-2.5 rounded-md px-2.5 py-1.5 text-sm leading-snug transition-colors',
                  active
                    ? 'bg-teal-50 font-semibold text-teal-800'
                    : 'text-ink-soft hover:bg-wash hover:text-ink',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-px font-mono text-[11px] tabular-nums',
                    active ? 'text-teal-500' : 'text-sage',
                  )}
                >
                  {sectionNumber(numberOf(section.id))}
                </span>
                <Icon
                  aria-hidden="true"
                  className={cn(
                    'mt-0.5 size-3.5 shrink-0',
                    active ? 'text-teal-500' : 'text-sage group-hover:text-teal-500',
                  )}
                />
                <span className="min-w-0">
                  {section.title}
                  {!inTitle ? (
                    <span className="block text-[11px] font-normal text-sage">match in text</span>
                  ) : null}
                </span>
              </a>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

interface MobileProps {
  matches: SectionMatch[]
  numberOf: (id: string) => number
  onJump: (id: string) => void
  query: string
  onQuery: (value: string) => void
}

/** Below 1024 px: the filter plus a "Jump to section" select instead of the side list. */
export function DocsMobileNav({ matches, numberOf, onJump, query, onQuery }: MobileProps) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4">
      <Select
        label="Jump to section"
        value=""
        placeholder={matches.length ? 'Choose a section…' : 'Nothing matches the filter'}
        options={matches.map(({ section }) => ({
          value: section.id,
          label: `${sectionNumber(numberOf(section.id))}  ${section.title}`,
        }))}
        onChange={(event) => {
          if (event.target.value) onJump(event.target.value)
        }}
      />
      <DocsFilter query={query} onQuery={onQuery} id="docs-filter-mobile" />
    </div>
  )
}
